import { Result, Space, Spin } from 'antd'
import { useCIMSDrawer } from 'components/CIMSDrawer'
import { useAuthContext } from 'context/AuthContext'
import { useProjectContext } from 'context/ProjectContext'
import { useRouteMatches } from 'hooks/useRouteMatches'
import { useCallback } from 'react'
import { useWorkflowRecordsQuery } from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import {
  createDisabledOptions,
  createTableActionDisabled,
} from './createDisabled'
import CreateWorkflowRecordDrawer from './components/CreateWorkflowRecordDrawer'
import RecordsTable from './components/RecordsTable'
import { useRecordsTable } from './hooks/useRecordsTable'
import type { RecordsWorkflow } from './types'

type RecordsContentProps = {
  loading: boolean
  onReload: ReturnType<typeof useWorkflowRecordsQuery>['refetch']
  workflow: RecordsWorkflow
}

function RecordsContent({ loading, onReload, workflow }: RecordsContentProps) {
  const { user } = useAuthContext()
  const { projectId } = useProjectContext()
  const drawer = useCIMSDrawer()
  const createOptions = workflow.createOptions
  const createDisabled = createTableActionDisabled(
    createDisabledOptions(createOptions)
  )
  const hasAvailableTargetStatus = createOptions.targetStatuses.some(
    ({ enabled, valid }) => enabled && valid
  )
  const projectAdmin = !!user && user.admin(projectId).authorized
  const canCreate =
    !!user &&
    (projectAdmin ||
      workflow.permissions.records.some(
        ({ action, users }) =>
          action === 'workflow_record.create' && users.includes(user.id)
      ))
  const canCreateComments =
    !!user &&
    (projectAdmin ||
      workflow.permissions.records.some(
        ({ action, users }) =>
          action === 'comment.create' && users.includes(user.id)
      ))
  const onCommentAdded = useCallback(() => onReload(), [onReload])
  const records = workflow.workflowRecords.records
  const { columns, expandable } = useRecordsTable(
    workflow.recordsTableConfig,
    records,
    {
      canCreate: canCreateComments,
      onCommentAdded,
    }
  )

  return (
    <>
      <RecordsTable
        actions={() => ({})}
        columns={columns}
        createAction={
          canCreate
            ? {
                disabled: createDisabled,
                label: createOptions.actionLabel,
                onClick: () => {
                  if (hasAvailableTargetStatus) drawer.open()
                },
              }
            : undefined
        }
        expandable={expandable}
        loading={loading}
        onReload={onReload}
        records={records}
      />
      {canCreate && (
        <CreateWorkflowRecordDrawer
          createOptions={createOptions}
          onClose={drawer.close}
          onCreated={() => onReload()}
          open={drawer.visible}
          workflowId={workflow.id}
        />
      )}
    </>
  )
}

/**
 * Loads one workflow's server-defined records table and renders its Technical
 * Directions records.
 */
function Records() {
  const { param } = useRouteMatches()
  const workflowId = param('workflowId')
  const { data, error, loading, refetch } = useWorkflowRecordsQuery({
    ...DefaultQueryHookOptions,
    skip: !workflowId,
    variables: workflowId ? { workflowId } : undefined,
  })
  const workflow = data?.listWorkflows.records[0]

  if (error)
    return (
      <Result
        status="error"
        title="Unable to load Technical Directions records"
        subTitle={error.message}
      />
    )

  if (!workflow && (loading || !workflowId))
    return (
      <Space>
        <Spin />
        <span>Loading Technical Directions records...</span>
      </Space>
    )

  if (!workflow) return <Result status="404" title="Workflow not found" />

  return (
    <RecordsContent loading={loading} onReload={refetch} workflow={workflow} />
  )
}

export default Records
